import { type ReactNode, createContext, useCallback, useContext, useEffect, useState } from "react";

import { ApiError, type AuthUser, type Lang, api } from "./api";
import { useLang } from "./i18n";
import {
  cacheUser,
  clearCachedUser,
  clearSessionToken,
  getCachedUser,
  getSessionToken,
  initNativeAuth,
} from "./native";

type AuthState =
  | { status: "loading"; user: null }
  | { status: "signed-out"; user: null }
  | { status: "signed-in"; user: AuthUser };

export type SignUpResult = { kind: "created" | "pending" };

export type AuthContextValue = {
  state: AuthState;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    name?: string,
    inviteCode?: string,
    language?: Lang,
  ) => Promise<SignUpResult>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
  // Optimistic local patch of the signed-in user. Used by per-user
  // preference setters (e.g. ADHD mode) that persist server-side
  // fire-and-forget but want the UI to reflect the change immediately.
  updateUser: (patch: Partial<AuthUser>) => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading", user: null });
  const { setLang } = useLang();

  // A signed-in user's account language wins over browser detection. This
  // covers every path that loads a user (refresh, signIn, signUp).
  useEffect(() => {
    if (state.status === "signed-in" && state.user.preferredLanguage) {
      setLang(state.user.preferredLanguage);
    }
  }, [state, setLang]);

  const refresh = useCallback(async () => {
    try {
      const me = await api.getMe();
      setState({ status: "signed-in", user: me.user });
      void cacheUser(me.user);
    } catch (err) {
      // Explicit auth failure: clear the native token + cached user.
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        void clearSessionToken();
        void clearCachedUser();
        setState({ status: "signed-out", user: null });
        return;
      }
      // Network blip or 5xx. On native, restore the last-known user from
      // Preferences so a cold launch with no signal does not bounce them to
      // the sign-in screen. On web getCachedUser returns null (no-op).
      //
      // Guard: only trust the cached user when we ALSO have a cached token.
      // Without this, a partial-clear state (cached user kept after token
      // expired) would treat the user as signed-in and trap them on the
      // Targets onboarding screen with no way out — reported by Sew on
      // v1.1.1 TestFlight: app launched straight into "Ustaw cel" and the
      // save crashed with 401 because no Bearer was attached.
      const cached = await getCachedUser();
      const token = getSessionToken();
      if (cached !== null && token !== null) {
        setState({ status: "signed-in", user: cached });
        return;
      }
      // Cached user without a token is junk left over from a previous
      // install / partial logout — wipe it so the next refresh starts clean.
      if (cached !== null && token === null) {
        void clearCachedUser();
      }
      setState((prev) =>
        prev.status === "signed-in" ? prev : { status: "signed-out", user: null },
      );
    }
  }, []);

  useEffect(() => {
    // Hydrate the native session token from Preferences before the first
    // /auth/me, so the request can authenticate on the very first launch.
    void (async () => {
      await initNativeAuth();
      await refresh();
    })();
  }, [refresh]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { user } = await api.signIn({ email, password });
    setState({ status: "signed-in", user });
    void cacheUser(user);
  }, []);

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      name?: string,
      inviteCode?: string,
      language?: Lang,
    ) => {
      const res = await api.signUp({
        email,
        password,
        ...(name ? { name } : {}),
        ...(inviteCode ? { inviteCode } : {}),
        ...(language ? { language } : {}),
      });
      if (res.kind === "created" && res.user) {
        setState({ status: "signed-in", user: res.user });
        void cacheUser(res.user);
      }
      // pending = email already existed (enumeration defense). Caller shows
      // a "check your inbox" message and keeps the user on signin screen.
      return { kind: res.kind };
    },
    [],
  );

  const signOut = useCallback(async () => {
    // Server-side logout is best-effort. If the API call throws (network
    // gone, 401 because the token already expired, JSON parse error on a
    // misrouted request) we MUST still complete the local cleanup —
    // otherwise the user can't escape the app on Targets-trap scenarios
    // like the v1.1.1 / v1.1.2 regression where the wrong base URL turned
    // every request into an HTML response.
    try {
      await api.signOut();
    } catch (err) {
      console.warn("[auth] signOut server call failed; clearing locally anyway", err);
    }
    await clearSessionToken();
    await clearCachedUser();
    setState({ status: "signed-out", user: null });
  }, []);

  const updateUser = useCallback((patch: Partial<AuthUser>) => {
    setState((prev) =>
      prev.status === "signed-in" ? { ...prev, user: { ...prev.user, ...patch } } : prev,
    );
  }, []);

  return (
    <AuthContext.Provider value={{ state, signIn, signUp, signOut, refresh, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
